import { createFileRoute } from "@tanstack/react-router";
import ogHomeImg from "@/assets/og/og-home.jpg";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ProductCard } from "@/components/ProductCard";
import { StorefrontPageShell } from "@/components/storefront/StorefrontPageShell";
import { listProducts } from "@/lib/catalog.functions";
import { bestSellersQuery } from "@/lib/queries/best-sellers";
import { SITE_URL, breadcrumbScript, ogImageMeta } from "@/lib/seo";

export const Route = createFileRoute("/best-sellers")({
  loader: ({ context }) => context.queryClient.ensureQueryData(bestSellersQuery()),
  head: ({ loaderData }) => {
    const url = `${SITE_URL}/best-sellers`;
    const list = Array.isArray(loaderData) ? (loaderData as any[]).slice(0, 20) : [];
    const description =
      "The parts Bangladeshi drivers, workshops and fleets buy most from Jantro Bazar.";
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      url,
      name: "Best-selling auto parts in Bangladesh",
      description,
      inLanguage: ["en", "bn"],
      isPartOf: { "@type": "WebSite", name: "Jantro Bazar", url: `${SITE_URL}/` },
    };
    const itemListLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      numberOfItems: list.length,
      itemListElement: list
        .filter((p) => p.slug)
        .map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${SITE_URL}/products/${p.slug}`,
          name: p.name_en || p.name || p.slug,
        })),
    };
    return {
      meta: [
        { title: "Best-selling auto parts in Bangladesh | Jantro Bazar" },
        { name: "description", content: description },
        { property: "og:title", content: "Best-selling auto parts" },
        { property: "og:description", content: "Most-ordered parts on Jantro Bazar." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        ...ogImageMeta(
          `${SITE_URL}${ogHomeImg}`,
          "Best-selling auto parts in Bangladesh | Jantro Bazar",
        ),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbScript("Best sellers", url),
        { type: "application/ld+json", children: JSON.stringify(collectionLd) },
        { type: "application/ld+json", children: JSON.stringify(itemListLd) },
      ],
    };
  },
  errorComponent: ({ error }) => (
    <div className="p-8 text-center text-sm text-muted-foreground">{error.message}</div>
  ),
  notFoundComponent: () => (
    <div className="p-8 text-center text-sm text-muted-foreground">
      No products yet. / এখনো কোনো পণ্য নেই।
    </div>
  ),
  component: BestSellers,
});

function BestSellers() {
  const fn = useServerFn(listProducts);
  const { data, isLoading, isError, error, refetch } = useSuspenseQuery({
    ...bestSellersQuery(),
    queryFn: () => fn({ data: { sort: "popular", limit: 60 } as any }),
  });
  const rows = (data as any[]) ?? [];
  return (
    <StorefrontPageShell
      testId="best-sellers-page"
      title="Best sellers"
      titleBn="সেরা বিক্রেতা"
      description="The parts most ordered on Jantro Bazar this month."
      descriptionBn="এই মাসে যন্ত্র বাজারে সবচেয়ে বেশি অর্ডার হওয়া পার্টস।"
      loading={isLoading}
      error={isError ? error : undefined}
      onRetry={() => void refetch()}
      rowCount={rows.length}
      emptyTitle="No best sellers yet"
      emptyTitleBn="এখনো কোনো সেরা বিক্রেতা নেই"
      emptyHint="Once orders start coming in, the most popular parts appear here."
      emptyHintBn="অর্ডার আসা শুরু হলে জনপ্রিয় পার্টস এখানে দেখা যাবে।"
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {rows.map((p: any) => (
          <ProductCard key={p.id ?? p.slug} p={p} />
        ))}
      </div>
    </StorefrontPageShell>
  );
}
